import { User } from './User'
import { SyncStatus } from '../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'

export type DestroyPolicyType = 'auto' | 'manual' | 'ttl'

export type AnonymousAccessType = 'allow' | 'deny' | 'inherit'

export type AutoDestroyPolicy = {
  type: 'auto'
}

export type ManualDestroyPolicy = {
  type: 'manual'
}

export type TTLDestroyPolicy = {
  type: 'ttl'
  expiresAt: string
}

export type DestroyPolicy = AutoDestroyPolicy | ManualDestroyPolicy | TTLDestroyPolicy

export type RoomStatus = 'created' | 'inactive' | 'active' | 'archived'

export interface SessionImportUser {
  type: 'anonymous' | 'managed'
}

export type RoomSessionConfig = {
  autoJoin: boolean
  autoJoinOverride: boolean
  isRecurring: boolean
  language?: RoomSessionLanguageConfig
  calculatedLanguage?: RoomSessionLanguageConfig
}

export type RoomSession = {
  id: string
  roomId: string
  startedAt: string
  finishedAt?: string
  isActive: boolean
  isPrivate: boolean
  kind: string
  metadata: string
  users: User[]
  config?: RoomSessionConfig
  roomCreatedBy?: UpdateIdentity
  syncStatus: SyncStatus[]
}

export enum MeetingLanguage {
  ENGLISH = 'en',
  SPANISH = 'es',
  FRENCH = 'fr',
  PORTUGUESE = 'pt',
  HINDI = 'hi',
  JAPANESE = 'ja',
  GERMAN = 'de',
  ITALIAN = 'it',
  RUSSIAN = 'ru',
  DUTCH = 'nl',
  AUTO_DETECT = 'auto-detect',
}

export enum LanguageConfigSource {
  TRANSCRIPT_AUTO_DETECT = 'transcript-auto-detect',
  CALCULATED_UNAMBIGUOUS = 'calculated-unambiguous',
  CALCULATED_MAJORITY = 'calculated-majority',
  CALCULATED_ACCOUNT_CONFIG = 'calculated-account-config',
  MANUAL = 'manual',
  NO_CLIENTS_ACCOUNT_CONFIG = 'no-clients-account-config',
  DEFAULT = 'default',
}

export type RoomSessionLanguageConfig = {
  language: MeetingLanguage
  source: LanguageConfigSource
  updatedAt?: string | null
}

export type RecurrenceRule = {
  frequency: string
  until?: string | undefined
  count?: number | undefined
  interval?: number | undefined
  byHour?: number[] | undefined
  byDay?: string[] | undefined
  byMonthDay?: number[] | undefined
  byYearDay?: number[] | undefined
  byWeekNumber?: number[] | undefined
  byMonth?: number[] | undefined
  bySetPosition?: number[] | undefined
  weekStart?: string | undefined
}

export type ExceptionRecurrenceRule = RecurrenceRule & {
  start?: string | undefined
}

export type Recurrence = {
  rules?: RecurrenceRule[] | undefined
  dates?: string[] | undefined
  exceptionRules?: ExceptionRecurrenceRule[] | undefined
  exceptionDates?: string[] | undefined
}

export type JoinWindow = {
  start?: string | undefined
  end?: string | undefined
  recurrence?: Recurrence | undefined
}

export interface RoomSessionEventMetadata {}

export interface JoinAttemptEventMetadata extends RoomSessionEventMetadata {
  joinRequest: {
    id: string
    name: string
    autoCreate: boolean
    attempt_with_password: boolean
  }
  joinResponse: {
    turnServers: Array<string>
    webSocketUrl: string
  }
}

export interface ParticipantJoinedEventMetadata {
  joinAttemptId: string
}

export interface AIFeatureEventMetadata extends RoomSessionEventMetadata {
  id: string
  name: string
}

export enum RoomSessionEventType {
  SESSION_STARTED = 'sessionStarted',
  SESSION_FINISHED = 'sessionFinished',
  PARTICIPANT_JOINED = 'participantJoined',
  PARTICIPANT_LEFT = 'participantLeft',
  JOIN_ATTEMPT = 'joinAttempt',
  VIDEO_MUTED = 'videoMuted',
  VIDEO_UNMUTED = 'videoUnmuted',
  AUDIO_MUTED = 'audioMuted',
  AUDIO_UNMUTED = 'audioUnmuted',
  SCREEN_SHARED = 'screenShared',
  SCREEN_UNSHARED = 'screenUnshared',
  SIGNAL_SENT = 'signalSent',
  TRACK_PUBLISHED = 'trackPublished',
  TRACK_UNPUBLISHED = 'trackUnpublished',
  AI_FEATURE_STARTED = 'aiFeatureStarted',
  AI_FEATURE_FINISHED = 'aiFeatureFinished',
}

export interface RoomSessionEvent {
  id: string
  sessionId: string
  type: RoomSessionEventType
  participantId: string | null
  timestamp: string
  metadata: RoomSessionEventMetadata
}

export interface JoinAttemptEvent extends RoomSessionEvent {
  type: RoomSessionEventType.JOIN_ATTEMPT
  participantId: string
  metadata: JoinAttemptEventMetadata
}

export interface ParticipantJoinedEvent extends RoomSessionEvent {
  type: RoomSessionEventType.JOIN_ATTEMPT
  participantId: string
  metadata: ParticipantJoinedEventMetadata
}

export type UpdateIdentity = {
  type: 'apiKey' | 'user'
  id: string
}

export type Room = {
  accountId: string
  id: string
  name?: string
  createdAt: string
  updatedAt: string
  createdBy: UpdateIdentity
  updatedBy: UpdateIdentity
  metadata: string
  status: RoomStatus
  config: {
    maxParticipants: number
    destroyPolicy: DestroyPolicy | undefined
    joinWindows: JoinWindow[] | undefined
    saveTranscription: boolean
    enhancedTranscription: boolean
    anonymousAccess?: AnonymousAccessType | undefined
  }
  activeSession?: {
    numberOfParticipants: number
    activeRecording: boolean
  }
  expiresAt?: string
  lastJoinedAt?: string
  lastActiveAt?: string
  password?: string
}

export type RoomCreatePayload = {
  name?: string
  password?: string | undefined
  metadata?: string | undefined
  config?:
    | {
        maxParticipants?: number
        destroyPolicy?: DestroyPolicy
        joinWindows?: JoinWindow[]
        anonymousAccess?: AnonymousAccessType
      }
    | undefined
}

export type AccountRoomConfiguration = {
  anonymousAccess: AnonymousAccessType
  saveTranscription: boolean
  enhancedTranscription: boolean
}

export type AccountRoomConfigurationPayload = {
  configOverride: {
    anonymousAccess: AnonymousAccessType
    saveTranscription: boolean
    enhancedTranscription: boolean
  }
}
